import { api, apiWithoutContentType, headers } from './client';

export const getMedicalConditionBasedOnBalagruha = async (payload) => {
  try {
    const body = Array.isArray(payload)
      ? { balagruhaIds: payload }
      : {
          ...(payload || {}),
          balagruhaIds: Array.isArray(payload?.balagruhaIds)
            ? payload.balagruhaIds
            : [],
        };

    const response = await api.post(
      "/api/medical-check-ins/students/list",
      body
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching medical conditions based on balagruha:", error);
    throw error;
  }
};

export const getMedicalCheckInsByStudentId = async (studentId) => {
  try {
    const response = await api.get(`/api/medical-check-ins/student/${studentId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching medical check-ins by student ID:", error);
    throw error;
  }
};

// The logged-in student's own check-ins (student-safe fields only) for the
// Learning Zone's Medical screen. The server scopes it by the token.
export const getMyMedicalCheckIns = async () => {
  try {
    const response = await api.get("/api/medical-check-ins/mine");
    return response.data;
  } catch (error) {
    console.error("Error fetching my medical check-ins:", error);
    throw error;
  }
};

export const getMoodBasedOnBalagruha = async (balagruhaIds) => {
  try {
    const response = await api.post("/api/v1/mood-tracker/latest", {
      balagruhaIds: Array.isArray(balagruhaIds) ? balagruhaIds : [],
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching mood based on balagruha:", error);
    throw error;
  }
};

export const createMedicalCheckin = async (data) => {
  try {
    // A FormData must go out as multipart. `api` forces a JSON content type,
    // which makes axios serialise the FormData to JSON — every attached File
    // becomes `{}` and never reaches the server.
    const response = data instanceof FormData
      ? await apiWithoutContentType.post("/api/medical-check-ins", data)
      : await api.post("/api/medical-check-ins", data, { headers });
    return response.data;
  } catch (error) {
    console.error("Error creating medical check-in:", error);
    throw error;
  }
};

export const updateMedicalCheckin = async (checkInId, data) => {
  try {
    const response = await api.put(`/api/medical-check-ins/${checkInId}`, data);
    return response.data;
  } catch (error) {
    console.error("Error updating medical check-in:", error);
    throw error;
  }
};

export const deleteMedicalCheckin = async (checkInId) => {
  try {
    const response = await api.delete(`/api/medical-check-ins/${checkInId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting medical check-in:", error);
    throw error;
  }
};

export const addMedicalCheckinAttachments = async (checkInId, formData) => {
  try {
    const response = await api.put(`/api/medical-check-ins/attachments/${checkInId}`, formData, { headers });
    return response.data;
  } catch (error) {
    console.error("Error adding medical check-in attachments:", error);
    throw error;
  }
};

export const deleteMedicalCheckinAttachment = async (checkInId, attachmentId) => {
  try {
    const response = await api.delete(`/api/medical-check-ins/attachments/${checkInId}/${attachmentId}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting medical check-in attachment:", error);
    throw error;
  }
};

export const getAllDoctors = async () => {
  try {
    const response = await api.get("/api/doctors");
    return response.data;
  } catch (error) {
    console.error("Error fetching doctors:", error);
    throw error;
  }
};

// Accepts either a string (legacy name-only call from the checkin dropdown)
// or a full doctor object { name, specialty, hospital, contactNumber }.
export const createDoctor = async (nameOrData) => {
  try {
    const payload = typeof nameOrData === "string" ? { name: nameOrData } : nameOrData;
    const response = await api.post("/api/doctors", payload);
    return response.data;
  } catch (error) {
    console.error("Error creating doctor:", error);
    throw error;
  }
};

// Update an existing doctor (Data Bank). Accepts a subset of fields.
export const updateDoctor = async (id, updates) => {
  try {
    const response = await api.put(`/api/doctors/${id}`, updates);
    return response.data;
  } catch (error) {
    console.error("Error updating doctor:", error);
    throw error;
  }
};

// Delete a doctor (Data Bank).
export const deleteDoctor = async (id) => {
  try {
    const response = await api.delete(`/api/doctors/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting doctor:", error);
    throw error;
  }
};

export const searchDoctors = async (searchTerm) => {
  try {
    const response = await api.get(`/api/doctors/search?q=${searchTerm}`);
    return response.data;
  } catch (error) {
    console.error("Error searching doctors:", error);
    throw error;
  }
};

export const getAllHospitals = async () => {
  try {
    const response = await api.get("/api/hospitals");
    return response.data;
  } catch (error) {
    console.error("Error fetching hospitals:", error);
    throw error;
  }
};

// Accepts either a string (legacy name-only call from the checkin dropdown)
// or a full hospital object/FormData (Hospital Bank's own Add/Edit form).
export const createHospital = async (nameOrData) => {
  try {
    const payload = typeof nameOrData === "string" ? { name: nameOrData } : nameOrData;
    const isFormData = payload instanceof FormData;
    const response = await api.post("/api/hospitals", payload, isFormData ? { headers } : undefined);
    return response.data;
  } catch (error) {
    console.error("Error creating hospital:", error);
    throw error;
  }
};

// Update an existing hospital (Hospital Bank). Accepts a plain object or FormData.
export const updateHospital = async (id, updates) => {
  try {
    const isFormData = updates instanceof FormData;
    const response = await api.put(`/api/hospitals/${id}`, updates, isFormData ? { headers } : undefined);
    return response.data;
  } catch (error) {
    console.error("Error updating hospital:", error);
    throw error;
  }
};

// Delete a hospital (Hospital Bank).
export const deleteHospital = async (id) => {
  try {
    const response = await api.delete(`/api/hospitals/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting hospital:", error);
    throw error;
  }
};

export const searchHospitals = async (searchTerm) => {
  try {
    const response = await api.get(`/api/hospitals/search?q=${searchTerm}`);
    return response.data;
  } catch (error) {
    console.error("Error searching hospitals:", error);
    throw error;
  }
};

export const getAllDiagnosticCenters = async () => {
  try {
    const response = await api.get("/api/diagnostic-centers");
    return response.data;
  } catch (error) {
    console.error("Error fetching diagnostic centers:", error);
    throw error;
  }
};

// Accepts a plain object or FormData (Doctor Bank's Add/Edit form).
export const createDiagnosticCenter = async (data) => {
  try {
    const isFormData = data instanceof FormData;
    const response = await api.post("/api/diagnostic-centers", data, isFormData ? { headers } : undefined);
    return response.data;
  } catch (error) {
    console.error("Error creating diagnostic center:", error);
    throw error;
  }
};

export const updateDiagnosticCenter = async (id, updates) => {
  try {
    const isFormData = updates instanceof FormData;
    const response = await api.put(`/api/diagnostic-centers/${id}`, updates, isFormData ? { headers } : undefined);
    return response.data;
  } catch (error) {
    console.error("Error updating diagnostic center:", error);
    throw error;
  }
};

export const deleteDiagnosticCenter = async (id) => {
  try {
    const response = await api.delete(`/api/diagnostic-centers/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error deleting diagnostic center:", error);
    throw error;
  }
};

export const createMood = async (data) => {
  try {
    const response = await api.post("/api/v1/mood-tracker", data);
    return response.data;
  } catch (error) {
    console.error("Error creating mood:", error);
    throw error;
  }
};
